const CHANNEL_KEY = '__kdcloud_kwc_pubsub_channel__';

// 使用更高效的ID生成方法
function genId() {
  return `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

class PubSubCore {
  constructor() {
    this.localSubs = new WeakMap();
    this.components = new Set();
    this.instanceId = genId();
    this.bc = null;
    this._listenBroadcast();
  }

  _listenBroadcast() {
    try {
      // 优先使用 BroadcastChannel
      this.bc = new BroadcastChannel(CHANNEL_KEY);
      this.bc.onmessage = (ev) => this._onMessage(ev.data);
    } catch {
      // 降级方案
      const messageHandler = (ev) => {
        const d = ev.data;
        if (d?.__KDCLOUD_KWC_PUBSUB__) this._onMessage(d);
      };
      window.addEventListener('message', messageHandler);
      
      // 保存引用以便后续清理（如果需要）
      this._messageHandler = messageHandler;
    }
  }

  _onMessage(data = {}) {
    const { event, payload, senderId } = data;
    // 使用短路运算简化条件判断
    if (!event || senderId === this.instanceId) return;
    this._emitLocal(event, payload, true);
  }

  _postBroadcast(event, payload) {
    const message = { 
      event, 
      payload, 
      senderId: this.instanceId,
      __KDCLOUD_KWC_PUBSUB__: true // 统一添加标识
    };
    
    try {
      if (this.bc) {
        this.bc.postMessage(message);
      } else {
        window.postMessage(message, '*');
      }
    } catch (error) {
      console.error('[my-pubsub] Broadcast failed:', error);
    }
  }

  _ensureFor(component) {
    if (!this.localSubs.has(component)) {
      this.localSubs.set(component, new Map());
      this.components.add(component);
    }
    return this.localSubs.get(component);
  }

  /**
   * 订阅事件
   * @param {Object} component - 组件实例
   * @param {string} event - 事件名称
   * @param {Function} callback - 回调函数
   * @returns {Function} 取消订阅函数
   */
  subscribe(component, event, callback) {
    // 参数校验
    if (typeof component !== 'object' || !component) {
      throw new TypeError('[my-pubsub] subscribe: 第一个参数必须是有效的组件实例');
    }
    if (typeof event !== 'string') {
      throw new TypeError('[my-pubsub] subscribe: event 必须是字符串');
    }
    if (typeof callback !== 'function') {
      throw new TypeError('[my-pubsub] subscribe: callback 必须是函数');
    }

    const subsForComp = this._ensureFor(component);
    if (!subsForComp.has(event)) {
      subsForComp.set(event, new Map());
    }

    const mapForEvent = subsForComp.get(event);
    const subId = genId();
    mapForEvent.set(subId, callback);

    // 返回取消订阅函数
    return () => {
      this._unsubscribeById(component, event, subId);
    };
  }

  /**
   * 内部方法：根据ID取消订阅
   */
  _unsubscribeById(component, event, subId) {
    const subs = this.localSubs.get(component);
    if (!subs) return;

    const evMap = subs.get(event);
    if (!evMap) return;

    evMap.delete(subId);
    
    // 清理空Map
    if (evMap.size === 0) {
      subs.delete(event);
    }
    if (subs.size === 0) {
      this._cleanup(component);
    }
  }

  /**
   * 取消订阅
   */
  unsubscribe(component, event, callbackOrId) {
    if (!component || typeof component !== 'object') return;
    
    const subs = this.localSubs.get(component);
    if (!subs) return;

    // 清理整个组件
    if (!event) {
      this._cleanup(component);
      return;
    }

    const evMap = subs.get(event);
    if (!evMap) return;

    // 未提供第三个参数：清理整个事件
    if (typeof callbackOrId === 'undefined') {
      subs.delete(event);
      if (subs.size === 0) this._cleanup(component);
      return;
    }

    // 根据类型处理
    if (typeof callbackOrId === 'string') {
      this._unsubscribeById(component, event, callbackOrId);
    } else if (typeof callbackOrId === 'function') {
      // 向后兼容：根据回调函数删除
      for (const [id, cb] of evMap.entries()) {
        if (cb === callbackOrId) {
          this._unsubscribeById(component, event, id);
          break; // 只删除第一个匹配的，避免误删
        }
      }
    }
  }

  publish(event, payload, { global = true } = {}) {
    if (typeof event !== 'string') {
      throw new TypeError('[my-pubsub] publish: event 必须是字符串');
    }
    
    // 先本地触发
    this._emitLocal(event, payload, false);
    
    // 再广播
    if (global) {
      this._postBroadcast(event, payload);
    }
  }

  _emitLocal(event, payload, fromRemote = false) {
    // 使用数组缓存避免在迭代过程中修改Set
    const components = Array.from(this.components);
    
    for (const comp of components) {
      const subsForComp = this.localSubs.get(comp);
      if (!subsForComp) {
        this.components.delete(comp);
        continue;
      }

      const evMap = subsForComp.get(event);
      if (!evMap) continue;

      // 使用数组缓存回调，避免在迭代过程中修改Map
      const callbacks = Array.from(evMap.values());
      
      for (const cb of callbacks) {
        try {
          cb(payload, { fromRemote });
        } catch (err) {
          console.error(`[my-pubsub] callback error in event "${event}":`, err);
        }
      }
    }
  }

  _cleanup(component) {
    try {
      this.localSubs.delete(component);
      this.components.delete(component);
    } catch (err) {
      console.warn('[my-pubsub] cleanup failed:', err);
    }
  }

  /**
   * 获取当前订阅状态（调试用）
   */
  getSubscriptionStats() {
    const stats = {
      totalComponents: this.components.size,
      events: new Map()
    };

    for (const comp of this.components) {
      const subs = this.localSubs.get(comp);
      if (subs) {
        for (const [event, eventMap] of subs.entries()) {
          if (!stats.events.has(event)) {
            stats.events.set(event, 0);
          }
          stats.events.set(event, stats.events.get(event) + eventMap.size);
        }
      }
    }

    return stats;
  }
}

// 单例模式
const core = new PubSubCore();

/* 导出 API */
export function subscribe(component, event, callback) {
  return core.subscribe(component, event, callback);
}

export function publish(event, payload, options) {
  return core.publish(event, payload, options);
}

export function unsubscribe(component, event, callbackOrId) {
  return core.unsubscribe(component, event, callbackOrId);
}

// 导出核心实例用于调试
export const __core__ = core;

export default { subscribe, publish, unsubscribe };